import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EbwModalService } from '@ebw-monorepo/modal';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { PsaService } from '../../../../services/psa.service';

/**
 * @Author Tommaso Lazzaretti
 *
 * DetailActionComponent
 *
 * This Angular component is responsible for handling the actions related to events.
 * It manages a form for creating or editing an action and allows the association of events
 * with the action using the form and data passed into the modal.
 *
 * Dependencies:
 * - MAT_DIALOG_DATA: Data passed to the component when it is opened as a dialog.
 * - MatDialogRef: Reference to the opened dialog, allowing it to be closed programmatically.
 * - PsaService: Service that contains API calls necessary for fetching events and managing actions.
 * - EbwModalService: Service that handles modal dialogs, such as showing error messages.
 */
@Component({
  selector: 'ebw-monorepo-detail-action',
  templateUrl: './detail-action.component.html',
  styleUrls: ['./detail-action.component.scss']
})
export class DetailActionComponent implements OnInit {
  @ViewChild('myInput') myInput: ElementRef<HTMLInputElement>;
  actionForm: FormGroup;

  inputValue = null;

  listEvents: any[] = [];
  associatedListEvents: any[] = [];
  riskEventsFiltered: any[] = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _formBuilder: FormBuilder,
    private _psaService: PsaService,
    private _dialogRef: MatDialogRef<DetailActionComponent>,
    private _ebwModalService: EbwModalService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this._psaService._getListEvents().subscribe((resp) => {
      this.listEvents = resp;

      if (this.data) {
        this.actionForm.patchValue({
          nome: this.data.nome || '', // Assegna valore se esiste
          descrizione: this.data.descrizione || '' // Assegna valore se esiste
        });

        // di un'azione esistente si può modificare solo la descrizione
        this.actionForm.get('nome')?.disable();

        this._psaService._getAction({ id: this.data.id }).subscribe((action) => {
          action.events.forEach((event) => {
            const item = this.listEvents.find((e) => e.id === event.evento_rischio_id);
            this.associatedListEvents.push(item);
          });
          this._filterOptions();
        });
      } else {
        this._filterOptions();
      }
    });

    this.actionForm = this._formBuilder.group({
      nome: ['', Validators.required],
      descrizione: ['']
    });
  }

  onInput(): void {
    this._filterOptions();
  }

  /**
   * Handles the selection of an option from the event list, adding it to the associated events list
   * and clearing the input field.
   */
  onOptionSelected(selectedValue): void {
    // Aggiungi l'elemento selezionato alla lista
    this.associatedListEvents.push(selectedValue);
    // Svuota il contenuto dell'input
    this.inputValue = null;
    this.myInput.nativeElement.value = null;

    this._filterOptions();
  }

  /**
   * Filters the list of available events, excluding the ones already associated with the action.
   */
  private _filterOptions(): void {
    const filterValue = this.inputValue ? this.inputValue.toLowerCase() : null;

    this.riskEventsFiltered = this.listEvents.filter((option) => !this.associatedListEvents.includes(option)); // Escludi gli elementi già selezionati

    if (filterValue)
      this.riskEventsFiltered = this.riskEventsFiltered.filter((option) => option.nome.toLowerCase().includes(filterValue.toLowerCase())); // Filtra in base al valore dell'input
  }

  /**
   * Removes an event from the list of associated events and updates the filtered options.
   */
  removeEvent(evento): void {
    this._ebwModalService
      .openConfirmModal({
        title: this.translateService.instant('common.delete'),
        content: this.translateService.instant('map.sidebar.psa.mitigating_action.delete_tooltip')
      })
      .afterClosed()
      .subscribe((result) => {
        if (result == true) {
          // Trova l'indice dell'elemento da rimuovere
          const index = this.associatedListEvents.findIndex((item) => item.id === evento.id);
          // Se l'elemento è presente nella lista, rimuovilo
          if (index > -1) {
            this.associatedListEvents.splice(index, 1);
          }

          // Svuota il contenuto dell'input direttamente
          this.myInput.nativeElement.value = null;
          this.inputValue = null;

          this._filterOptions();
        }
      });
  }

  onCancel(): void {
    this._dialogRef.close();
  }

  /**
   * Saves the action by either updating an existing action (events and description) or creating a new one,
   * and attaches the associated events to it.
   * Shows an error modal if the operation fails.
   */
  async onSave(): Promise<void> {
    try {
      const params = {
        nome: this.actionForm.value.nome,
        descrizione: this.actionForm.value.descrizione,
        evento_rischio_ids: this.getIdsRiskEvents()
      };

      if (this.data && this.data.id) {
        const request = this._psaService._attachActionToEvent({
          id: this.data.id,
          evento_rischio_ids: this.getIdsRiskEvents(),
          descrizione: this.actionForm.value.descrizione
        });
        await lastValueFrom(request);
        this._ebwModalService.openInfoModal({
          title: this.translateService.instant('map.sidebar.psa.mitigating_action.save_title'),
          content: this.translateService.instant('common.save_successful')
        });
        this._dialogRef.close('ATTACHED');
        return;
      }

      const request = this._psaService._addAction(params);
      await lastValueFrom(request);
      this._dialogRef.close('ADDED');
    } catch (error) {
      console.error('[DetailAction][onSave] ', error);
      this._ebwModalService.openErrorModal({
        title: this.translateService.instant('map.sidebar.psa.mitigating_action.save_title'),
        content: error?.error?.message
      });
    }
  }

  /**
   * Retrieves the IDs of the associated events.
   *
   * @returns {string[]} Array of event IDs.
   */
  getIdsRiskEvents(): string[] {
    return this.associatedListEvents.map((item) => item.id.toString());
  }
}
